import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  RewardsService,
  TCreateRewardCodesPayload,
  TImportRewardCodesPayload,
} from "./rewards.service";
import toast from "react-hot-toast";

const useGetList = (page: number, queryParams?: Record<string, any>) => {
  const { limit: pageSize, ...filters } = queryParams ?? {};
  return useQuery(
    ["rewards.list", page, filters, pageSize],
    async () => {
      const { data } = await RewardsService.getListRewards({
        page,
        ...(pageSize ? { per_page: pageSize } : {}),
        ...filters,
      });
      return data;
    },
    { keepPreviousData: true }
  );
};

const useGetTotalByUser = (userId: string) => {
  return useQuery(
    ["rewards.totalByUser", userId],
    async () => {
      const { data } = await RewardsService.getTotalByUser({ user_id: userId });
      return data;
    },
    { enabled: !!userId }
  );
};

const useGetDetail = (rewardId: string) => {
  return useQuery(
    ["rewards.detail", rewardId],
    async () => {
      const { data } = await RewardsService.getDetailReward(rewardId);
      return data;
    },
    { enabled: !!rewardId }
  );
};

const toIsoFromLocalDatetime = (value: unknown) => {
  const str = String(value ?? "");
  if (!str) return str;
  const date = new Date(str);
  return isNaN(date.getTime()) ? str : date.toISOString();
};

const useCreate = (onSuccessCallback?: (data: any) => void) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (formValues: any) => {
      const payload: TCreateRewardCodesPayload = {
        reward_program_id: String(formValues?.reward_program_id).trim(),
        reward_type_code: String(formValues?.reward_type_code).trim(),
        value: Number(formValues?.value),
        quantity_codes: Number(
          formValues?.quantity_codes ?? formValues?.quantity
        ),
        expiry_datetime: toIsoFromLocalDatetime(formValues?.expiry_datetime),
      };

      return await RewardsService.createRewardCodes(payload);
    },
    {
      onSuccess: (responseData) => {
        if (responseData?.ok) {
          toast.success("Create successfully!");
          queryClient.invalidateQueries(["rewards.list"]);
          onSuccessCallback?.(responseData);
        } else {
          toast.error(responseData?.msg);
        }
      },
      onError(err: any) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Create failed";
        toast.error(errorMessage);
      },
    }
  );
};

const useAssignReward = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ rewardId, userId }: { rewardId: string; userId: string }) => {
      return await RewardsService.assignReward(rewardId, userId);
    },
    {
      onSuccess: (responseData: any) => {
        if (responseData?.ok) {
          toast.success("Assigned successfully!");
          queryClient.invalidateQueries(["rewards.list"]);
          onSuccessCallback?.();
        } else {
          toast.error(responseData?.msg);
        }
      },
      onError(err: any) {
        const errorMessage =
          err?.response?.data?.msg ||
          err?.response?.data?.error ||
          err?.message ||
          "Assign failed";
        toast.error(errorMessage);
      },
    }
  );
};

const useImportRewardCodes = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (formData: TImportRewardCodesPayload) => {
      return await RewardsService.importRewardCodes(formData);
    },
    {
      onSuccess: ( responseData: any) => {
        if(responseData?.ok) {
          toast.success("Import request submitted");
          queryClient.invalidateQueries(["rewards.list"]);
          onSuccessCallback?.();
        } else {
          toast.error(responseData?.msg || "Import failed");
        }
      },
      onError(err: any) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Import failed";
        toast.error(errorMessage);
      },
    }
  );
};

const useMarkAsUsed = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ rewardId, userId }: { rewardId: string; userId: string }) => {
      return await RewardsService.markAsUsed(rewardId, userId);
    },
    {
      onSuccess: (responseData: any) => {
        if (responseData?.ok) {
          toast.success("Marked as used!");
          queryClient.invalidateQueries(["rewards.list"]);
          onSuccessCallback?.();
        } else {
          toast.error(responseData?.msg);
        }
      },
      onError(err: any) {
        const errorMessage =
          err?.response?.data?.msg ||
          err?.response?.data?.error ||
          err?.message ||
          "Mark as used failed";
        toast.error(errorMessage);
      },
    }
  );
};

  const useAssignRewardToChargeSession = (onSuccessCallback?: () => void) => {
    const queryClient = useQueryClient();

    return useMutation(
      async ({ rewardId, chargeSessionId }: { rewardId: string; chargeSessionId: string }) => {
        return await RewardsService.assignRewardToChargeSession(rewardId, chargeSessionId);
      },
      {
        onSuccess: (responseData: any) => {
          if (responseData?.ok) {
            toast.success("Assigned reward successfully!");
            queryClient.invalidateQueries(["rewards.list"]);
            onSuccessCallback?.();
          } else {
            toast.error(responseData?.msg || "Assign failed");
          }
        },
        onError(err: any) {
          const errorMessage =
            err?.response?.data?.msg ||
            err?.response?.data?.error ||
            err?.message ||
            "Assign failed";
          toast.error(errorMessage);
        },
      }
    );
  };

export {
  useGetList,
  useGetDetail,
  useCreate,
  useGetTotalByUser,
  useAssignReward,
  useImportRewardCodes,
  useMarkAsUsed,
  useAssignRewardToChargeSession, 
};
